// components/shared.js - Shared UI components
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSync } from '../contexts/SyncContext';
import { useLang } from '../contexts/LanguageContext';
import api from '../utils/api';

// ── Smart back ────────────────────────────────────────────────────────────────
// Back buttons must go exactly one screen back, not to the home screen. Only
// when there is no in-app history (deep link / fresh load) fall back to home.
// React Router keeps its history index in window.history.state.idx.
export function useSmartBack(fallback = '/') {
  const navigate = useNavigate();
  return () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate(fallback);
  };
}

// ── Sync Status Bar ───────────────────────────────────────────────────────────
const OFFLINE_READY_TTL_HOURS = 24;

export function SyncBar() {
  const {
    isOnline, syncStatus, isSyncing, lastSync, syncNow,
    isWarming, lastWarmup, warmOfflineCache, keepAwake, syncResult, uploadWindow,
  } = useSync();
  const { t } = useLang();

  const warmupFresh = lastWarmup && (Date.now() - lastWarmup.getTime()) < OFFLINE_READY_TTL_HOURS * 60 * 60 * 1000;
  const offlineReady = warmupFresh;

  const statusColor = !isOnline
    ? (offlineReady ? 'var(--accent-success)' : 'var(--accent-warning)')
    : syncStatus.failed > 0 ? 'var(--accent-danger)'
    : syncStatus.pending > 0 ? 'var(--accent-warning)'
    : 'var(--accent-success)';

  const statusText = !isOnline
    ? (offlineReady ? '✈ Offline · ready for shift' : '⚡ Offline — limited (cache empty)')
    : isSyncing ? '⟳ Syncing...'
    : syncStatus.failed > 0 ? `⚠ ${syncStatus.failed} not uploaded yet — retrying`
    : syncStatus.pending > 0 ? `↑ ${syncStatus.pending} pending`
    : '✓ Synced';

  // Outside the factory's sync window photos wait on the tablet on purpose.
  const windowHolding = uploadWindow?.enabled && !uploadWindow.open && uploadWindow.waiting > 0;
  const fill = (key, values) => Object.entries(values)
    .reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));
  const resultText = !syncResult ? null
    : syncResult.error && syncResult.left === null ? `${t('sync.result.failed')}: ${syncResult.error}`
    : syncResult.error ? fill('sync.result.error', { left: syncResult.left })
    : syncResult.sent === 0 && syncResult.left === 0 ? t('sync.result.nothing')
    : fill('sync.result.sent', { sent: syncResult.sent, left: syncResult.left });

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 16px', background: 'var(--bg-tertiary)',
      borderBottom: '1px solid var(--border-subtle)', fontSize: '0.75rem',
      color: statusColor, fontWeight: 500, gap: 12, flexWrap: 'wrap'
    }}>
      <span>
        {isSyncing ? t('sync.busy') : resultText || statusText}
        {windowHolding && !isSyncing && !resultText && (
          <span title={t('sync.window.hint')} style={{ marginLeft: 8, color: 'var(--text-muted)', fontWeight: 400 }}>
            {fill('sync.window.waiting', { time: uploadWindow.opensAt, count: uploadWindow.waiting })}
          </span>
        )}
        {keepAwake && (
          <span title="Leave the tablet on its charger with the app open; uploads continue and the screen switches off by itself once everything is synced."
            style={{ marginLeft: 8, color: 'var(--text-muted)', fontWeight: 400 }}>
            ☀ screen stays on until uploaded
          </span>
        )}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isOnline && (
          <button
            onClick={warmOfflineCache}
            disabled={isWarming}
            title={lastWarmup ? `Last prepared ${lastWarmup.toLocaleString()}` : 'Cache reference data for offline use'}
            style={{
              background: 'transparent', border: '1px solid currentColor',
              color: offlineReady ? 'var(--accent-success)' : 'var(--accent-warning)',
              cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
              padding: '2px 8px', borderRadius: 4,
            }}
          >
            {isWarming ? '⟳ preparing…' : offlineReady ? '✓ offline ready' : '⤓ prepare offline'}
          </button>
        )}
        {lastSync && <span style={{ color: 'var(--text-muted)' }}>
          {lastSync.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>}
        <button onClick={() => syncNow(true)} disabled={isSyncing} style={{
          background: 'transparent', border: 'none', color: statusColor,
          cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, padding: '2px 6px'
        }}>
          {isSyncing ? t('sync.busy') : t('sync.now')}
        </button>
      </div>
    </div>
  );
}

// ── Order sync badge ──────────────────────────────────────────────────────────
// Order-level sync state: green once all 6 core steps are on the server
// (with the moment that happened), amber while photos are still missing.
export function OrderSyncBadge({ order, showTime = false }) {
  if (order.synced_steps == null) return null;
  const complete = !!order.sync_complete;
  const color = complete ? 'var(--accent-success)'
    : order.synced_steps > 0 ? 'var(--accent-warning)' : 'var(--text-muted)';
  const label = complete
    ? (showTime && order.sync_completed_at
        ? `✓ Synced ${formatSyncTime(order.sync_completed_at)}`
        : '✓ Synced')
    : `${order.synced_steps}/6 synced`;
  return (
    <span title={complete && order.sync_completed_at
      ? `All 6 photos on the server since ${formatSyncTime(order.sync_completed_at)}`
      : 'Photos still missing on the server (not shot yet, or waiting on the tablet to sync)'}
      style={{
        fontSize: '0.6875rem', fontWeight: 600, color,
        border: `1px solid ${color}`, borderRadius: 4, padding: '1px 6px',
        whiteSpace: 'nowrap',
      }}>
      {label}
    </span>
  );
}

// SQLite datetimes are UTC without a timezone marker; append Z so the tablet
// shows factory-local time.
function formatSyncTime(dt) {
  const d = new Date(dt.includes('T') || dt.endsWith('Z') ? dt : dt.replace(' ', 'T') + 'Z');
  return d.toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// ── Confirm Dialog ────────────────────────────────────────────────────────────
export function ConfirmDialog({ open, title, message, confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <h3 style={{ marginBottom: 12 }}>{title}</h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary btn-full" onClick={onCancel}>Cancel</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'} btn-full`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Autocomplete Input ────────────────────────────────────────────────────────
export function AutocompleteInput({ value, onChange, placeholder, endpoint, className }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!value || value.length < 1) { setSuggestions([]); setOpen(false); return; }
    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get(endpoint, { params: { q: value } });
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch {} finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(timeout);
  }, [value, endpoint]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`input-field ${className || ''}`}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: 'var(--bg-secondary)', border: '1px solid var(--border-normal)',
          borderRadius: 'var(--radius-md)', marginTop: 4,
          boxShadow: 'var(--shadow-lg)', overflow: 'hidden', maxHeight: 200, overflowY: 'auto'
        }}>
          {loading && <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Searching...</div>}
          {suggestions.map((s, i) => (
            <div key={i} onMouseDown={() => { onChange(s); setOpen(false); }} style={{
              padding: '10px 14px', cursor: 'pointer', fontSize: '0.9375rem',
              transition: 'background 0.1s', borderBottom: i < suggestions.length-1 ? '1px solid var(--border-subtle)' : 'none'
            }} className="card-hover">{s}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Photo Thumbnail ───────────────────────────────────────────────────────────
export function PhotoThumb({ photo, onClick, showLabel = true }) {
  const [imgError, setImgError] = useState(false);
  const serverBase = process.env.REACT_APP_API_URL || '';

  // Grid uses the small 400x300 thumbnail for speed; full-res only loads in the viewer.
  const fullSrc = photo.server_path ? `${serverBase}${photo.server_path}` : (photo.local_data || null);
  const imgSrc = photo.thumbnail_filename || photo.thumb || fullSrc;

  const syncColors = { synced: '#34c97d', pending: '#f5a623', failed: '#f45858' };
  // Review label → badge. Photos needing a re-shoot get a red outline.
  const REVIEW_BADGES = {
    to_discuss: { label: 'To discuss', color: '#f5a623' },
    feedback_requested: { label: 'Feedback', color: '#f45858' },
    resolved: { label: 'Resolved', color: '#34c97d' },
  };
  const reviewBadge = REVIEW_BADGES[photo.review_status];
  const needsReshoot = photo.review_status === 'feedback_requested';

  if (photo.is_na) {
    return (
      <div className="photo-thumb" onClick={onClick} style={{ background: 'var(--bg-tertiary)' }}>
        <div className="photo-thumb-na">N/A</div>
        {showLabel && <div className="photo-thumb-label">{photo.step_name}</div>}
      </div>
    );
  }

  return (
    <div className="photo-thumb" onClick={onClick}
      style={needsReshoot ? { outline: '3px solid #f45858', outlineOffset: -3 } : undefined}>
      {imgSrc && !imgError ? (
        <img src={imgSrc} alt={photo.step_name} onError={() => setImgError(true)} loading="lazy" />
      ) : (
        <div style={{ width: '100%', height: '100%', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '2rem' }}>📷</div>
      )}
      <div className="photo-thumb-sync" style={{ background: syncColors[photo.sync_status] || '#555' }}>
        {photo.sync_status === 'synced' ? '✓' : photo.sync_status === 'failed' ? '✗' : '…'}
      </div>
      {reviewBadge && (
        <div style={{
          position: 'absolute', top: 6, left: 6, padding: '2px 7px', borderRadius: 6,
          fontSize: '0.6875rem', fontWeight: 700, color: '#fff', zIndex: 1,
          background: reviewBadge.color,
        }}>{reviewBadge.label}</div>
      )}
      {photo.annotation_count > 0 && (
        <div title={`${photo.annotation_count} comment${photo.annotation_count === 1 ? '' : 's'}`} style={{
          position: 'absolute', bottom: 6, left: 6, padding: '2px 7px', borderRadius: 10,
          fontSize: '0.6875rem', fontWeight: 700, color: '#fff', zIndex: 1,
          background: 'rgba(79,142,247,0.95)', display: 'flex', alignItems: 'center', gap: 3,
        }}>💬 {photo.annotation_count}</div>
      )}
      {showLabel && <div className="photo-thumb-label">{photo.step_name || photo.step_label}</div>}
    </div>
  );
}

// ── Comment Thread ────────────────────────────────────────────────────────────
export function CommentThread({ comments, orderId, rdProjectId, onNewComment }) {
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/comments', {
        order_id: orderId, rd_project_id: rdProjectId,
        parent_id: replyTo?.id || null, body: body.trim()
      });
      onNewComment(data);
      setBody('');
      setReplyTo(null);
    } catch (err) {
      alert('Failed to post comment');
    } finally { setSubmitting(false); }
  };

  const topLevel = comments.filter(c => !c.parent_id);
  const getReplies = (id) => comments.filter(c => c.parent_id === id);

  const initials = name => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const renderComment = (comment, isReply = false) => (
    <div key={comment.id} className={`comment-item ${isReply ? 'comment-reply' : ''}`}>
      <div className="comment-avatar">{initials(comment.author_name)}</div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{comment.author_name}</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {new Date(comment.created_at).toLocaleString()}
          </span>
        </div>
        <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{comment.body}</p>
        <button onClick={() => setReplyTo(comment)} style={{
          background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer',
          fontSize: '0.75rem', fontWeight: 600, marginTop: 4, padding: 0
        }}>Reply</button>
        {getReplies(comment.id).map(r => renderComment(r, true))}
      </div>
    </div>
  );

  return (
    <div>
      {topLevel.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', padding: '12px 0' }}>No comments yet</div>
      )}
      {topLevel.map(c => renderComment(c))}
      <div style={{ marginTop: 16 }}>
        {replyTo && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 6 }}>
            Replying to {replyTo.author_name} · <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', color: 'var(--accent-danger)', cursor: 'pointer', fontSize: '0.8125rem' }}>Cancel</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <textarea className="input-field" value={body} onChange={e => setBody(e.target.value)}
            placeholder="Add a comment..." style={{ flex: 1, minHeight: 70, resize: 'none' }}
            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) submit(); }}
          />
          <button className="btn btn-primary btn-icon" onClick={submit} disabled={!body.trim() || submitting}
            style={{ alignSelf: 'flex-end' }}>
            {submitting ? '…' : '↑'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Checklist ─────────────────────────────────────────────────────────────────
export function OrderChecklist({ checklist, orderId, onUpdate }) {
  const toggle = async (item) => {
    try {
      await api.post('/api/checklist-responses', {
        order_id: orderId, template_id: item.id, checked: !item.checked
      });
      onUpdate(item.id, !item.checked);
    } catch {}
  };

  return (
    <div>
      {checklist.map(item => (
        <div key={item.id} className="checklist-item">
          <div className={`checkbox-custom ${item.checked ? 'checked' : ''}`} onClick={() => toggle(item)}>
            {item.checked && '✓'}
          </div>
          <span style={{ fontSize: '0.9375rem', color: item.checked ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Flag Chips ─────────────────────────────────────────────────────────────────
export function FlagChips({ order, onChange, readOnly = false }) {
  const flags = [
    { key: 'has_claim', label: 'Has Claim', cls: 'active-claim' },
    { key: 'used_for_marketing', label: 'Marketing', cls: 'active-marketing' },
    { key: 'flagged_by_management', label: 'Management', cls: 'active-management' },
  ];

  return (
    <div className="tag-list">
      {flags.map(f => (
        <div key={f.key}
          className={`flag-chip ${order[f.key] ? f.cls : ''}`}
          onClick={() => !readOnly && onChange && onChange({ ...order, [f.key]: !order[f.key] })}
          style={{ cursor: readOnly ? 'default' : 'pointer' }}
        >
          {f.label}
        </div>
      ))}
    </div>
  );
}

// ── Loading Screen ─────────────────────────────────────────────────────────────
export function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-logo">PhotoApp</div>
      <div className="spinner spinner-lg" />
    </div>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────────
export function EmptyState({ icon = '📷', title, message, action }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon}</div>
      {title && <h3>{title}</h3>}
      {message && <p>{message}</p>}
      {action}
    </div>
  );
}

// ── Section Header ─────────────────────────────────────────────────────────────
export function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div className="section-title">{title}</div>
      {action}
    </div>
  );
}
